import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { LogEntry } from "features/CenterContent/VehicleDetails/shared/LogEntry";
import React, { useState } from "react";
import { DismissibleInfoMessage } from "shared/components/feedback/InfoMessage/DismissibleInfoMessage";
import { Button, Textarea, tokens } from "shared/components/Spor";
import {
  AlternativeTransportLog,
  AlternativeTransportLogReq,
} from "shared/types/alternativeTransport";
import { compareDateStringAsc } from "shared/utils/dateTimeUtils";
import styled from "styled-components";

const inputMsg = "Skriv loggmelding";
const OperationalLogWrapper = styled.div<{
  $noInternalScroll?: boolean;
  $disablePadding?: boolean;
}>`
  display: flex;
  flex-direction: column;
  overflow-y: ${({ $noInternalScroll }) =>
    $noInternalScroll ? "visible" : "auto"};
  width: 100%;
  padding: ${({ $disablePadding }) =>
    $disablePadding ? "0" : tokens.default.size.spacing[2]};
`;

const LogList = styled.ul<{ $noInternalScroll?: boolean }>`
  overflow-y: ${({ $noInternalScroll }) =>
    $noInternalScroll ? "visible" : "scroll"};
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  row-gap: ${tokens.default.size.spacing[2]};
`;

type OperationalLogProps = {
  vehicleId: string;
  logs: AlternativeTransportLog[];
  distributionStopPlaces?: string;
  textareaMinHeight?: string;
  /** When true, the log scrolls with its parent container instead of internally */
  disableInternalScroll?: boolean;
  /** When true, removes the wrapper padding (e.g. when rendered inside a Module that already pads) */
  disablePadding?: boolean;
};

const OperationalLog = ({
  vehicleId,
  logs,
  distributionStopPlaces,
  textareaMinHeight,
  disableInternalScroll,
  disablePadding,
}: OperationalLogProps) => {
  const [logMessage, setLogMessage] = useState("");

  const operationalLogMutation = useMutation({
    mutationKey: ["postAlternativeTransportLogs", vehicleId],
    mutationFn: (data: AlternativeTransportLogReq) =>
      mutationFnPOST<AlternativeTransportLog, AlternativeTransportLogReq>(
        `${getBackendUrl()}/alternativeTransportLogs/${vehicleId}`,
        data,
      ),
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    operationalLogMutation.mutate({
      message: logMessage,
      distributionStopPlaces: distributionStopPlaces ?? "",
      alternativeTransportId: vehicleId,
    });

    setLogMessage("");
  }

  const getDisplayMessage = (message: string | null, tags: string[]) =>
    [message, ...tags].filter(Boolean).join(tags.length > 0 ? ". " : " ");

  return (
    <OperationalLogWrapper
      $noInternalScroll={disableInternalScroll}
      $disablePadding={disablePadding}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: tokens.default.size.spacing[2],
          marginBottom: tokens.default.size.spacing[2],
        }}
      >
        <Textarea
          key={vehicleId}
          size="sm"
          fontSize={["mobile.xs", "desktop.xs"]}
          fontWeight="normal"
          pt={2}
          label={inputMsg}
          value={logMessage}
          onChange={(e) => {
            setLogMessage(e.target.value);
          }}
          minH={textareaMinHeight ?? "10rem"}
        />
        <DismissibleInfoMessage
          semantics="alarm"
          summary="Kunne ikke sende loggmelding. Prøv på nytt eller kontakt IT."
          isDismissed={operationalLogMutation.status !== "error"}
          onDismiss={operationalLogMutation.reset}
        />
        <Button
          size="sm"
          variant="primary"
          type="submit"
          loading={operationalLogMutation.status === "pending"}
          disabled={logMessage.length === 0}
          ml="auto"
        >
          Send melding
        </Button>
      </form>
      <LogList $noInternalScroll={disableInternalScroll}>
        {logs
          .sort((a, b) => compareDateStringAsc(a.timestamp, b.timestamp))
          .map(
            ({ id, timestamp, message, sourceSystem, locationName, tags }) => (
              <LogEntry
                key={id}
                timestamp={timestamp}
                message={getDisplayMessage(message, tags)}
                source={locationName}
                fallbackSource={sourceSystem.name}
              />
            ),
          )}
      </LogList>
    </OperationalLogWrapper>
  );
};

export default OperationalLog;
